import { useMatchRoute } from '@tanstack/react-router'
import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '#components/Icon'
import { Link } from '#components/Link'
import { useSidebar } from '#hooks/useSidebar'

import type { IProps } from './interfaces'
import styles from './styles.module.scss'

/** Stable test id derived from the route, independent of the translated label. */
export function navTestIdFromLocation(location: string): string {
  const slug = location
    .split('/')
    .filter((segment): boolean => segment.length > 0 && !segment.startsWith('$'))
    .join('-')
    .toLocaleLowerCase()

  return slug.length > 0 ? `nav-${slug}` : 'nav-root'
}

export const NavigationItem = ({
  label,
  location,
  icon,
  badge,
  isChild,
  isDisabled,
  exact,
  onClick,
}: IProps): React.JSX.Element => {
  const { t } = useTranslation()
  const matchRoute = useMatchRoute()
  const { isCollapsed, isMobile, setCollapsed } = useSidebar()

  // `fuzzy: true` is the default so a list-page nav item stays active
  // while the user drills into a detail view. Opt-out via `exact`
  // where a sibling route shares the prefix (e.g. `/invoicing` vs
  // `/invoicing/lines`) and both items would otherwise light up.
  const isActive = !isDisabled && Boolean(matchRoute({ to: location, fuzzy: exact !== true }))
  const dataTestId = navTestIdFromLocation(location)

  const handleClick = (): void => {
    onClick?.()
    if (isMobile) setCollapsed(true)
  }

  return (
    <div
      data-testid={dataTestId}
      className={clsx(
        styles.navigationItem,
        isActive && styles.active,
        isCollapsed && styles.collapsed,
        isChild && styles.child,
        isDisabled && styles.disabled,
      )}
      data-active={isActive}
      title={isCollapsed ? label : undefined}
    >
      <Link
        className={styles.link}
        href={isDisabled ? '#' : location}
        withUnderline={false}
        ariaLabel={t('{{ label }} navigation item', { label })}
        onClick={isDisabled ? undefined : handleClick}
      >
        {/* The expanded sidebar has no icons (DS); the collapsed rail shows only the icon */}
        {icon && isCollapsed && (
          <span className={styles.iconBox} data-testid={`${dataTestId}-icon`}>
            <Icon name={icon} color="currentColor" width="20" height="20" />
          </span>
        )}
        <span className={styles.label}>{label}</span>
        {badge && (
          <span className={styles.badge} data-testid={`${dataTestId}-badge`}>
            {badge}
          </span>
        )}
      </Link>
    </div>
  )
}
